import {useContext} from 'react';
import {useQuery} from '@tanstack/react-query';
import {useShallow} from 'zustand/react/shallow';
import {CarecoApiContext} from '@/app-context/careco-api-context';
import {useAuthStore} from '@/store/auth_store';
import {useNotificationStore} from '@/store/notification_store';
import {useNotifications} from '@/hooks/useNotifications';
import WidgetPopover from '@common/widgets/popover/WidgetPopover';
import WidgetOnlyShowUnread from '@common/widgets/WidgetOnlyShowUnread';
import NotificationsWidgetIcon from '@/components/pages/notifications/NotificationsWidgetIcon';
import NotificationPopoverItem from '@/components/pages/notifications/popover/NotificationPopoverItem';

export default function NotificationsWidget() {
  const { carecoApi } = useContext(CarecoApiContext);
  const currentUser = useAuthStore((state) => state.currentUser);

  const [popoverOpen, setPopoverOpen] = useNotificationStore(useShallow((state) => [
    state.popoverOpen,
    state.setPopoverOpen,
  ]));

  const { data: currentFetchedUser } = useQuery({
    queryKey: ['currentUser', currentUser?.id],
    queryFn: () => carecoApi?.getUser(currentUser?.id ?? ''),
    enabled: popoverOpen && !!currentUser && !!carecoApi,
  });

  const {data: rosters} = useQuery({
    queryKey: ['rosters'],
    queryFn: () => carecoApi?.getRosters(),
    enabled: !!carecoApi,
  });

  const {
    isApiLoading,
    isFetched,
    error,
    notifications,
    totalNotifications,
    totalUnreadNotifications,
    markAllAsRead
  } = useNotifications({
    onlyShowUnreadNotifications: currentFetchedUser?.only_show_unread_notifications
      ?? currentUser?.only_show_unread_notifications
      ?? false,
    enabled: popoverOpen && !!currentUser,
  });

  return (
    <WidgetPopover
      isOpen={popoverOpen}
      handleClose={setPopoverOpen}
      elements={notifications}
      totalElements={totalNotifications}
      totalUnreadElements={totalUnreadNotifications}
      dateProperty="notificationDate"
      title="Notifications"
      route="/notifications"
      count={3}
      isLoading={!isFetched}
      isApiLoading={isApiLoading}
      error={error}
      markAllAsRead={markAllAsRead}
      render={(notification) => (
        <NotificationPopoverItem
          roster={rosters?.data.data.find(roster => roster.id === notification.group.id)}
          key={notification.id}
          notification={notification}
        />
      )}
      header={currentFetchedUser && (
        <WidgetOnlyShowUnread
          queryKey={(currentUser) => ['notifications', currentUser.only_show_unread_notifications]}
          currentFetchedUser={currentFetchedUser}
          property="only_show_unread_notifications"
          label="Notifications"
        />
      )}
    >
      <NotificationsWidgetIcon />
    </WidgetPopover>
  );
}
